import { useQuery } from "@tanstack/react-query";
import { getPlayerStatistics, LEAGUE_IDS, type PlayerStatistics } from "@/lib/apiSports";
import { useAuth } from "@/contexts/AuthContext";
import type { FavoritePlayer } from "@/hooks/useUserFavorites";

export interface LivePlayerStats {
  playerId: number;
  league: string;
  gp: number;
  goals: number;
  assists: number;
  points: number;
  plusMinus: number;
  pim: number;
  // Goalie stats
  wins?: number;
  losses?: number;
  gaa?: number;
  svPct?: number;
  shutouts?: number;
  isGoalie: boolean;
}

// Map league name to API league ID
const getLeagueId = (league: string): number | null => {
  const leagueMap: Record<string, number> = {
    NHL: LEAGUE_IDS.NHL,
    AHL: LEAGUE_IDS.AHL,
    ECHL: LEAGUE_IDS.ECHL,
    KHL: LEAGUE_IDS.KHL,
    SHL: LEAGUE_IDS.SHL,
    Allsvenskan: LEAGUE_IDS.ALLSVENSKAN,
    Liiga: LEAGUE_IDS.LIIGA,
    Extraliga: LEAGUE_IDS.EXTRALIGA,
    DEL: LEAGUE_IDS.DEL,
    NL: LEAGUE_IDS.NL,
  };
  return leagueMap[league] || null;
};

// Get current season (year)
const getCurrentSeason = (): number => {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const year = now.getFullYear();
  // If August-December, use current year; if January-July, use previous year
  return month >= 7 ? year : year - 1;
};

// Transform API response to our stats format - using any for flexible API response
const transformPlayerStats = (apiData: PlayerStatistics): LivePlayerStats | null => {
  const stats = apiData.statistics?.[0] as Record<string, unknown> | undefined;
  if (!stats) return null;

  const games = stats.games as { appearences?: number; minutes?: number; position?: string } | undefined;
  const goalsData = stats.goals as { total?: number; assists?: number; against?: number; saves?: number } | undefined;
  
  const isGoalie = games?.position === "G";
  const goals = goalsData?.total || 0;
  const assists = goalsData?.assists || 0;

  if (isGoalie) {
    const goalsAgainst = goalsData?.against || 0;
    const saves = goalsData?.saves || 0;
    const gp = games?.appearences || 0;
    const minutes = games?.minutes || 0;
    const gaa = minutes > 0 ? (goalsAgainst / minutes) * 60 : 0;
    const shotsAgainst = goalsAgainst + saves;
    const svPct = shotsAgainst > 0 ? saves / shotsAgainst : 0;

    return {
      playerId: apiData.player.id,
      league: (stats.league as { name?: string })?.name || "",
      gp,
      goals: 0,
      assists: 0,
      points: 0,
      plusMinus: 0,
      pim: 0,
      wins: (stats as Record<string, unknown>).wins as number || 0,
      losses: (stats as Record<string, unknown>).losses as number || 0,
      gaa,
      svPct,
      shutouts: (stats as Record<string, unknown>).shutouts as number || 0,
      isGoalie: true,
    };
  }

  const penalty = stats.penalty as { minutes?: number } | undefined;

  return {
    playerId: apiData.player.id,
    league: (stats.league as { name?: string })?.name || "",
    gp: games?.appearences || 0,
    goals,
    assists,
    points: goals + assists,
    plusMinus: (stats.plusminus as number) || 0,
    pim: penalty?.minutes || 0,
    isGoalie: false,
  };
};

// Fetch stats for a single player
async function fetchPlayerStats(
  playerId: number,
  league: string
): Promise<LivePlayerStats | null> {
  const leagueId = getLeagueId(league);
  if (!leagueId) return null;

  const season = getCurrentSeason();

  try {
    const response = await getPlayerStatistics({
      league: leagueId,
      season,
      player: playerId,
    });

    if (response.response && response.response.length > 0) {
      return transformPlayerStats(response.response[0]);
    }
    return null;
  } catch (error) {
    console.error(`Failed to fetch stats for player ${playerId}:`, error);
    return null;
  }
}

// Batch fetch stats for multiple players
async function fetchMultiplePlayerStats(
  players: FavoritePlayer[]
): Promise<Map<string, LivePlayerStats>> {
  const results = new Map<string, LivePlayerStats>();

  // Group players by league for more efficient API calls
  const playersByLeague = new Map<string, FavoritePlayer[]>();
  players.forEach((player) => {
    const existing = playersByLeague.get(player.league) || [];
    existing.push(player);
    playersByLeague.set(player.league, existing);
  });

  // Fetch stats for each league (with rate limiting consideration)
  const fetchPromises: Promise<void>[] = [];

  playersByLeague.forEach((leaguePlayers, league) => {
    leaguePlayers.forEach((player) => {
      fetchPromises.push(
        fetchPlayerStats(player.id, league).then((stats) => {
          if (stats) {
            results.set(`${player.league}-${player.id}`, stats);
          }
        })
      );
    });
  });

  await Promise.allSettled(fetchPromises);
  return results;
}

export function useFavoritePlayerStats(players: FavoritePlayer[]) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["favoritePlayerStats", players.map((p) => `${p.league}-${p.id}`).join(",")],
    queryFn: () => fetchMultiplePlayerStats(players),
    enabled: players.length > 0 && !!user, // Only fetch if user is logged in
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export default useFavoritePlayerStats;
